import { getResolvedFrontendGraphqlUrls } from './resolveFrontendGraphqlUrls';
import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { GraphQLClient } from 'graphql-request';
import { getSegmentAnonymousIdFromCookie } from '@doxyme/frontend/features/tracking/trackers/segment/cookies.segment';

const getGraphEndpoint = (): string => {
  try {
    return getResolvedFrontendGraphqlUrls().graphqlUrl;
  } catch (error) {
    console.error('Failed to resolve GraphQL endpoint:', error);
    throw new Error('GraphQL configuration is missing or invalid');
  }
};

const graphEndpoint = getGraphEndpoint();
// Authorization header will be added by useUpdateApiHeaders hook once the user is authenticated
export const client = new GraphQLClient(graphEndpoint);

// Store base headers (like Authorization) separately since headers is now a function
let baseHeaders: Record<string, string> = {};

/** Segment may set `ajs_anonymous_id` before `analytics.user().anonymousId()` is available. */
const unwrapQuotedCookieValue = (value: string): string | undefined => {
  const t = value.trim();
  if (!t) {
    return undefined;
  }
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    const inner = t.slice(1, -1).trim();
    return inner || undefined;
  }
  return t;
};

const getAnonymousId = (): string | undefined => {
  const { analytics } = window;
  const fromAnalytics = analytics?.user?.()?.anonymousId?.();
  if (typeof fromAnalytics === 'string' && fromAnalytics.trim() !== '') {
    return fromAnalytics.trim();
  }
  const fromCookie = getSegmentAnonymousIdFromCookie();
  return fromCookie ? unwrapQuotedCookieValue(fromCookie) : undefined;
};

// Set headers as a function that dynamically includes anonymousId
// This function is called on every request, ensuring anonymousId is always up-to-date
// It merges anonymousId with base headers (like Authorization)
client.requestConfig.headers = () => {
  // Include anonymousId if available
  const anonymousId = getAnonymousId();
  if (anonymousId) {
    return {
      ...baseHeaders,
      'x-segment-anonymous-id': anonymousId,
    };
  }

  return baseHeaders;
};

export const graphQlService = createApi({
  reducerPath: 'apolloApi',
  baseQuery: graphqlRequestBaseQuery({ client }),
  tagTypes: ['TenantUser'],
  endpoints: () => ({}),
});

export const updateGraphQLAuthHeader = (bearer: string) => {
  // Update base headers instead of using setHeaders (since headers is now a function)
  baseHeaders = {
    ...baseHeaders,
    Authorization: bearer,
  };
};
